import React from 'react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
        <div className="prose prose-indigo max-w-none">
          <p className="text-sm text-gray-600 mb-6">Effective Date: 1/11/2025</p>
          
          <p className="mb-6">Welcome to StoryMuse.io! These Terms of Service ("Terms") govern your access to and use of the StoryMuse.io website and services (the "Platform"). By accessing or using the Platform, you agree to these Terms.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">1. General Overview</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">1.1 Agreement</h3>
          <p className="mb-4">By using StoryMuse.io, you acknowledge that you have read, understood, and agree to these Terms. If you do not agree, do not use the Platform.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">1.2 Eligibility</h3>
          <p className="mb-4">You must be at least 18 years old, or the legal age in your jurisdiction, to use this Platform. By using the Platform, you represent that you meet these eligibility requirements.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">1.3 Changes to the Terms</h3>
          <p className="mb-4">StoryMuse.io reserves the right to update or modify these Terms at any time. We will notify users of significant changes via email or through the Platform. Your continued use of the Platform after such modifications constitutes acceptance of those changes.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">2. User Accounts</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">2.1 Account Creation</h3>
          <p className="mb-4">When creating an account, you must provide accurate and complete information. You are responsible for maintaining the accuracy of this information.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.2 Account Security</h3>
          <p className="mb-4">You are responsible for:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Immediately notifying us of any unauthorized use of your account</li>
            <li>Ensuring that you log out from your account at the end of each session</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">3. User Content</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">3.1 Content Ownership</h3>
          <p className="mb-4">You retain all ownership rights to the content you post on StoryMuse.io. By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, copy, modify, publish, and distribute your content for the purpose of operating and improving the Platform.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.2 Content Guidelines</h3>
          <p className="mb-4">All content must:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Be original or properly attributed</li>
            <li>Not infringe on any third-party rights</li>
            <li>Not contain hate speech, harassment, or explicit content</li>
            <li>Not promote illegal activities</li>
            <li>Not contain malware or malicious code</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.3 Content Removal</h3>
          <p className="mb-4">We reserve the right to remove any content that violates these Terms or that we determine to be harmful to the Platform or its users.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">4. Intellectual Property Rights</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">4.1 Platform Content</h3>
          <p className="mb-4">The Platform's content, features, and functionality are owned by StoryMuse.io and are protected by copyright, trademark, and other intellectual property laws.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">4.2 User Rights</h3>
          <p className="mb-4">Users retain all rights to their original content. However, by posting content, you grant other users the right to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>View and interact with your content through the Platform's features</li>
            <li>Share your content within the Platform</li>
            <li>Provide feedback and comments on your content</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">5. Privacy and Data Protection</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">5.1 Data Collection</h3>
          <p className="mb-4">We collect and process personal data as described in our Privacy Policy. By using the Platform, you consent to such processing and warrant that all data provided by you is accurate.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">5.2 Data Security</h3>
          <p className="mb-4">We implement appropriate technical and organizational measures to protect your personal data but cannot guarantee absolute security.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">6. Termination</h2>
          <h3 className="text-lg font-semibold mt-6 mb-2">6.1 Account Termination</h3>
          <p className="mb-4">We may suspend or terminate your account at any time for violations of these Terms or for any other reason we deem appropriate.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">6.2 Effect of Termination</h3>
          <p className="mb-4">Upon termination:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Your access to the Platform will be revoked</li>
            <li>Your content may remain on the Platform unless you request its removal</li>
            <li>These Terms will continue to apply to any past use of the Platform</li>
          </ul>

          <h2 className="text-xl font-bold mt-8 mb-4">7. Limitation of Liability</h2>
          <p className="mb-4">StoryMuse.io and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Platform.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">8. Governing Law</h2>
          <p className="mb-4">These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which StoryMuse.io is registered, without regard to its conflict of law provisions.</p>

          <h2 className="text-xl font-bold mt-8 mb-4">Contact Information</h2>
          <p className="mb-4">For questions about these Terms or to report violations, please contact us at:</p>
          <p className="mb-4">Email: support@storymuse.io</p>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <Link to="/auth" className="text-indigo-600 hover:text-indigo-800">
              ← Back to Sign Up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}